// What the device page's Residents and Queue tabs ask cma-api for, and how they show what comes back. Pure functions, no React and no requests.

export const STATUSES = ['queued', 'running', 'completed', 'error', 'resolved'];
const query = (o) => new URLSearchParams(Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== undefined && v !== null))).toString();

export const residentsUrl = (account, deviceType, deviceId) => `cma/endpointdevice/results?${query({ account, deviceType, deviceId })}`;
export const residentUrl = (account, deviceType, deviceId, name) => `cma/endpointdevice/results?${query({ account, deviceType, deviceId, name })}`;
export const queueUrl = (account, deviceType, deviceId, ymd, status) => `cma/endpointdevice/queue?${query({ account, deviceType, deviceId, ymd, status })}`;
export const RUN_NOW_URL = 'cma/endpointdevice/runnow';
export const scheduleUrl = (account, deviceType, deviceId) => `cma/endpointdevice/schedule?${query({ account, deviceType, deviceId })}`;
export const liveUrl = (account, deviceType, deviceId) => `cma/endpointdevice/residentlive?${query({ account, deviceType, deviceId })}`;
export const logUrl = (account, deviceType, deviceId, name, lines) => `cma/endpointdevice/residentlog?${query({ account, deviceType, deviceId, name, lines })}`;
export const jobUrl = (account, deviceType, deviceId, id) => `cma/endpointdevice/queue?${query({ account, deviceType, deviceId, id })}`;

// The day a job is filed under is the UTC day it was made.
export const todayYmd = (now = new Date()) => now.toISOString().slice(0, 10);
export const shiftYmd = (ymd, days) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const two = (n) => String(n).padStart(2, '0');
// A time VES gave as seconds since 1970 or as a date text, in the viewer's own time zone and 12-hour clock.
export const when = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const d = new Date(typeof value === 'number' ? value * 1000 : value);
  if (Number.isNaN(d.getTime())) return String(value);
  const h = d.getHours() % 12 || 12;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}, ${h}:${two(d.getMinutes())}:${two(d.getSeconds())} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
};

export const took = (ms) => (typeof ms === 'number' ? `${(ms / 1000).toFixed(1)} s` : '');
export const sizeText = (bytes) => (typeof bytes === 'number' ? `${bytes} B` : '');

export const JOB_TONES = { completed: 'ok', error: 'err', running: 'warn', queued: 'none', resolved: 'none' };

// A job's results and splat arrive as text or as objects; show an object either way, and text that is not JSON as it is.
export const asValue = (v) => {
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch (ex) { return v; }
};

// The line a resident result is shown under: its name, status and when and how long it ran.
export const residentSummary = (item) => [
  item.name || '', item.status || '', item.startedUtc ? `run started ${when(item.startedUtc)}` : '', item.durationMs !== undefined && item.durationMs !== null ? `took ${took(item.durationMs)}` : '',
  sizeText(item.resultsBytes), item.agentVersion ? `agent ${item.agentVersion}` : '',
].filter(Boolean).join(' · ');

// ---- the Residents tab: how a script's schedule, state and log are put in words

export const SCRIPT_TONES = { applied: 'ok', pending: 'none', skipped: 'err', paused: 'warn' };
export const SCRIPT_TITLES = {
  applied: 'The device has this script scheduled.',
  pending: 'The device has not applied the current schedule yet. It applies it when it next connects.',
  paused: 'This script is paused in its schedule.',
  skipped: 'The device refused this script.',
};
export const SYNC_TONES = { applied: 'ok', errors: 'warn', pending: 'none' };
export const SYNC_TEXT = { applied: 'applied', errors: 'applied with errors', pending: 'out of date' };
export const LIVE_TONES = { running: 'ok', idle: 'none', paused: 'warn', 'not scheduled': 'none' };

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const numbers = (field, max) => {
  if (!/^\d+(,\d+)*$/.test(field)) return null;
  const list = field.split(',').map(Number);
  return list.every((n) => n <= max) ? list : null;
};
const clock = (h, m) => `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
const joined = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

// A cron in plain words, for the common shapes ("At 8:00 AM, 12:00 PM and 4:00 PM, Monday through Friday", "Every 30 minutes"); '' for the rest.
// The device runs it in its own local time.
export const describeCron = (cron) => {
  const fields = String(cron || '').trim().split(/\s+/);
  if (fields.length !== 5) return '';
  const [minute, hour, dom, month, dow] = fields;
  if (dom !== '*' || month !== '*') return '';
  let what;
  const step = /^\*\/(\d+)$/.exec(minute);
  if (minute === '*' && hour === '*') what = 'Every minute';
  else if (step && hour === '*') what = `Every ${step[1]} minutes`;
  else {
    const minutes = numbers(minute, 59);
    const hours = numbers(hour, 23);
    if (!minutes || !hours) return '';
    what = `At ${joined(hours.flatMap((h) => minutes.map((m) => clock(h, m))))}`;
  }
  if (dow === '*') return what;
  const range = /^([0-7])-([0-7])$/.exec(dow);
  if (range) return `${what}, ${DAYS[Number(range[1]) % 7]} through ${DAYS[Number(range[2]) % 7]}`;
  const days = numbers(dow, 7);
  return days ? `${what}, on ${joined(days.map((d) => DAYS[d % 7]))}` : '';
};

// "5 min", "45 s", "1 min 30 s"
export const describeDelay = (seconds) => {
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  return s % 60 === 0 ? `${s / 60} min` : `${Math.floor(s / 60)} min ${s % 60} s`;
};

// A schedule's filter as chips' text: "account is myorg".
export const filterText = (filter) => (filter?.conditions || []).map((c) => {
  const value = c.op === 'in' ? (c.values || []).join(' or ') : c.value;
  const verb = { eq: 'is', in: 'is', contains: 'contains', startswith: 'starts with' }[c.op] || c.op;
  return `${c.key} ${verb} ${value}`;
});

export const bytesText = (n) => {
  if (typeof n !== 'number') return '';
  if (n < 1024) return `${n} B`;
  return n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
